import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { toastMessage } from '@ui/components/Toast';
import type { SignatureRequestFormData, ConsentTemplate, SignedConsent, MockCustomer, MockPet } from '../types';

export interface QuestionAnswerData {
    displayOrder: number;
    answer: string | number;
}

export function useSignature(
    templates: ConsentTemplate[],
    customers: MockCustomer[],
    onComplete: (consent: SignedConsent) => void,
    onSignConsent?: (params: { userId: string; templateId: number; signatureDataUrl: string; petId?: string; questionAnswers?: QuestionAnswerData[] }) => Promise<void>
) {
    // 모달 상태
    const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
    const [signatureStep, setSignatureStep] = useState<'select' | 'sign' | 'preview'>('select');

    // 폼 데이터
    const [signatureFormData, setSignatureFormData] = useState<SignatureRequestFormData>({
        customerId: '',
        petId: '',
        templateId: '',
    });

    // 선택된 펫 정보 (API에서 가져온 데이터)
    const [selectedPetInfo, setSelectedPetInfo] = useState<MockPet | null>(null);

    // 서명 데이터
    const [signatureDataUrl, setSignatureDataUrl] = useState<string>('');
    const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    // questionAnswers 데이터 (복수 선택형은 string[] 지원)
    const [questionAnswers, setQuestionAnswers] = useState<Record<string, string | number | string[]>>({});

    // 모든 템플릿 사용 가능 (isMandatory는 필수/선택 여부만 나타냄)
    const activeTemplates = useMemo(() => templates, [templates]);

    // 선택된 고객
    const selectedCustomer = useMemo(() => {
        return customers.find((c) => c.id === signatureFormData.customerId);
    }, [customers, signatureFormData.customerId]);

    // 선택된 반려동물
    const selectedPet = useMemo(() => {
        const customer = customers.find((c) => c.id === signatureFormData.customerId);
        return customer?.pets.find((p) => p.id === signatureFormData.petId);
    }, [customers, signatureFormData.customerId, signatureFormData.petId]);

    // 선택된 템플릿
    const selectedTemplate = useMemo(() => {
        return templates.find((t) => String(t.id) === signatureFormData.templateId);
    }, [templates, signatureFormData.templateId]);

    // 선택된 고객의 반려동물 목록
    const selectedCustomerPets = useMemo(() => {
        return selectedCustomer?.pets || [];
    }, [selectedCustomer]);

    // 이전 고객 ID를 추적하는 ref
    const prevCustomerIdRef = useRef<string>('');

    // 고객이 변경되면 반려동물 선택 초기화
    useEffect(() => {
        const currentCustomerId = signatureFormData.customerId;
        // 고객이 실제로 변경되었고, 이전 고객이 있었던 경우에만 초기화
        if (prevCustomerIdRef.current && prevCustomerIdRef.current !== currentCustomerId) {
            setSignatureFormData((prev) => ({ ...prev, petId: '' }));
            setSelectedPetInfo(null);
        }
        prevCustomerIdRef.current = currentCustomerId;
    }, [signatureFormData.customerId]);

    // 서명 데이터 URL이 blob인 경우 정리
    useEffect(() => {
        return () => {
            // 컴포넌트 언마운트 시 blob URL 정리 (만약 있다면)
            if (signatureDataUrl && signatureDataUrl.startsWith('blob:')) {
                URL.revokeObjectURL(signatureDataUrl);
            }
        };
    }, []);

    // 모달 열기
    const openSignatureModal = useCallback(() => {
        setSignatureStep('select');
        setSignatureFormData({ customerId: '', petId: '', templateId: '' });
        setSignatureDataUrl('');
        setSelectedPetInfo(null);
        setQuestionAnswers({});
        setIsSignatureModalOpen(true);
    }, []);

    // 템플릿 선택과 함께 모달 열기
    const openSignatureModalWithTemplate = useCallback((templateId: string) => {
        setSignatureStep('select');
        setSignatureFormData({ customerId: '', petId: '', templateId });
        setSignatureDataUrl('');
        setSelectedPetInfo(null);
        setQuestionAnswers({});
        setIsSignatureModalOpen(true);
    }, []);

    // 모달 닫기
    const closeSignatureModal = useCallback(() => {
        setIsSignatureModalOpen(false);
        setSignatureStep('select');
        setSignatureFormData({ customerId: '', petId: '', templateId: '' });
        setSignatureDataUrl('');
        setSelectedPetInfo(null);
        setQuestionAnswers({});
    }, []);

    // 다음 단계로
    const goToNextStep = useCallback(() => {
        if (signatureStep === 'select') {
            if (!signatureFormData.customerId || !signatureFormData.templateId) {
                toastMessage({ message: '모든 항목을 선택해주세요.', type: 'warning' });
                return;
            }
            setSignatureStep('sign');
            // 캔버스 초기화
            setTimeout(() => {
                const canvas = signatureCanvasRef.current;
                if (canvas) {
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                    }
                }
            }, 100);
        } else if (signatureStep === 'sign') {
            if (!signatureDataUrl) {
                toastMessage({ message: '서명을 입력해주세요.', type: 'warning' });
                return;
            }
            setSignatureStep('preview');
        }
    }, [signatureStep, signatureFormData, signatureDataUrl]);

    // 이전 단계로
    const goToPrevStep = useCallback(() => {
        if (signatureStep === 'sign') {
            setSignatureStep('select');
            setSignatureDataUrl('');
        } else if (signatureStep === 'preview') {
            setSignatureStep('sign');
        }
    }, [signatureStep]);

    // 서명 완료
    const completeSignature = useCallback(async () => {
        const customer = customers.find((c) => c.id === signatureFormData.customerId);
        const template = templates.find((t) => String(t.id) === signatureFormData.templateId);

        // selectedPetInfo를 우선 사용하고, 없으면 formData.petId 확인
        if (!customer || !template) {
            toastMessage({ message: '선택 정보가 올바르지 않습니다.', type: 'error' });
            return;
        }

        // questionAnswers를 API 형식으로 변환 (복수 선택형은 배열을 쉼표로 구분된 문자열로 변환)
        const formattedQuestionAnswers: QuestionAnswerData[] = Object.entries(questionAnswers).map(([_questionId, answer], index) => ({
            displayOrder: index,
            answer: Array.isArray(answer) ? answer.join(',') : answer,
        }));

        // API 호출이 있으면 실행
        if (onSignConsent) {
            try {
                await onSignConsent({
                    userId: customer.id,
                    templateId: template.id,
                    signatureDataUrl,
                    questionAnswers: formattedQuestionAnswers.length > 0 ? formattedQuestionAnswers : undefined,
                });
                closeSignatureModal();
            } catch {
                // 에러는 onSignConsent에서 처리
            }
        } else {
            // API가 없으면 로컬에서 처리
            const newConsent: SignedConsent = {
                id: String(Date.now()),
                templateId: String(template.id),
                templateName: template.name,
                customerName: customer.name,
                customerPhone: customer.phone,
                petName: selectedPetInfo?.name || '반려동물',
                petSpecies: selectedPetInfo?.species || '',
                petBreed: selectedPetInfo?.breed || '',
                signedAt: new Date().toISOString(),
                signatureImageUrl: signatureDataUrl,
            };

            onComplete(newConsent);
            toastMessage({ message: '동의서가 저장되었습니다.', type: 'success' });
            closeSignatureModal();
        }
    }, [customers, signatureFormData, signatureDataUrl, templates, selectedPetInfo, questionAnswers, onComplete, onSignConsent, closeSignatureModal]);

    // 캔버스 그리기 시작
    const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        // 캔버스 내부 크기와 화면 표시 크기 간의 스케일 팩터 계산
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        let x: number, y: number;

        if ('touches' in e) {
            x = (e.touches[0].clientX - rect.left) * scaleX;
            y = (e.touches[0].clientY - rect.top) * scaleY;
        } else {
            x = (e.clientX - rect.left) * scaleX;
            y = (e.clientY - rect.top) * scaleY;
        }

        ctx.beginPath();
        ctx.moveTo(x, y);
    }, []);

    // 캔버스 그리기
    const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;

        const canvas = signatureCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        // 캔버스 내부 크기와 화면 표시 크기 간의 스케일 팩터 계산
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        let x: number, y: number;

        if ('touches' in e) {
            e.preventDefault();
            x = (e.touches[0].clientX - rect.left) * scaleX;
            y = (e.touches[0].clientY - rect.top) * scaleY;
        } else {
            x = (e.clientX - rect.left) * scaleX;
            y = (e.clientY - rect.top) * scaleY;
        }

        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000000';
        ctx.lineTo(x, y);
        ctx.stroke();
    }, [isDrawing]);

    // 캔버스 그리기 종료
    const stopDrawing = useCallback(() => {
        setIsDrawing(false);
        const canvas = signatureCanvasRef.current;
        if (canvas) {
            setSignatureDataUrl(canvas.toDataURL('image/png'));
        }
    }, []);

    // 서명 지우기
    const clearSignature = useCallback(() => {
        const canvas = signatureCanvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                setSignatureDataUrl('');
            }
        }
    }, []);

    // 폼 데이터 업데이트
    const updateFormData = useCallback((updates: Partial<SignatureRequestFormData>) => {
        setSignatureFormData((prev) => ({ ...prev, ...updates }));
    }, []);

    return {
        // State
        isSignatureModalOpen,
        signatureStep,
        signatureFormData,
        signatureDataUrl,
        signatureCanvasRef,
        isDrawing,
        activeTemplates,
        selectedCustomer,
        selectedPet,
        selectedTemplate,
        selectedCustomerPets,
        customers,
        selectedPetInfo,
        questionAnswers,

        // Actions
        openSignatureModal,
        openSignatureModalWithTemplate,
        closeSignatureModal,
        goToNextStep,
        goToPrevStep,
        completeSignature,
        startDrawing,
        draw,
        stopDrawing,
        clearSignature,
        updateFormData,

        // Setters (for direct JSX control)
        setSignatureFormData,
        setSignatureStep,
        setIsSignatureModalOpen,
        setSelectedPetInfo,
        setQuestionAnswers,
    };
}
